import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { OfflineStore } from '../offline/db';

/**
 * The offline store, opened for the signed-in worker and shared by the screens.
 *
 * Opened here rather than per screen because opening is what enforces the
 * shared-handset rule: `OfflineStore.openFor` destroys the database when it
 * finds it belongs to a different employee, is from an older schema, or has
 * sat unopened past the idle limit. Doing that once per session, at the point
 * identity becomes known, is the whole of the guarantee.
 *
 * `store` is null until it opens, and stays null where IndexedDB is
 * unavailable — a private window, a browser blocking site data. Screens treat
 * that as "no cached copy", never as an error: the app has to work online on a
 * handset with no usable storage.
 */

interface OfflineState {
  store: OfflineStore | null;
  /** The browser's own reachability signal. Optimistic, and worth saying so:
   *  `navigator.onLine` means "attached to a network", not "the API answers".
   *  It is a hint for wording, never the thing a fetch decision is made on. */
  online: boolean;
}

const Ctx = createContext<OfflineState>({ store: null, online: true });

export function OfflineProvider(
  { employeeId, children }: { employeeId: string; children: ReactNode },
) {
  const [store, setStore] = useState<OfflineStore | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    let live = true;
    let opened: OfflineStore | null = null;

    OfflineStore.openFor(employeeId).then((s) => {
      if (!live) { s?.close(); return; }
      opened = s;
      setStore(s);
    });

    return () => {
      live = false;
      opened?.close();
      setStore(null);
    };
  }, [employeeId]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const value = useMemo(() => ({ store, online }), [store, online]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOffline(): OfflineState {
  return useContext(Ctx);
}
